import {
    customiseLabelsOptions,
    TCustomiseLabelsOption,
    TCustomiseLabelsOptions,
    TCustomiseLabelsRule,
    TOptionsIds,
} from "./CustomiseLabels.config";

export type TCustomiseLabelsDisabledOptions = Partial<Record<TOptionsIds, boolean>>;

/**
 * Builds a map of group ids to option ids for a specific rule type.
 *
 * @example
 * // Output, here we are grouping options with the "some-enabled" rule:
 * // { "roles-names": ["show-roles", "show-names"] }
 */
const getGroupIdsByRule = (
    rulesConfig: readonly TCustomiseLabelsOption[],
    ruleType: TCustomiseLabelsRule["type"]
): Record<string, TOptionsIds[]> => {
    return rulesConfig.reduce((groups, option) => {
        option.rules?.forEach((rule) => {
            if (rule.type !== ruleType) {
                return;
            }

            if (!groups[rule.groupId]) {
                groups[rule.groupId] = [];
            }

            groups[rule.groupId].push(option.id as TOptionsIds);
        });

        return groups;
    }, {} as Record<string, TOptionsIds[]>);
};

/**
 * Normalizes options according to rules.
 * For "some-enabled", ensures at least one option stays enabled.
 * If all are false, it enables the first id in that group.
 *
 * @example
 * const input = { "show-names": false, "show-roles": false };
 * const output = applyCustomiseLabelsRules(input);
 * // output: { "show-names": false, "show-roles": true }
 */
export const applyCustomiseLabelsRules = (
    options: TCustomiseLabelsOptions,
    rulesConfig: readonly TCustomiseLabelsOption[] = customiseLabelsOptions
): TCustomiseLabelsOptions => {
    const normalizedOptions = { ...options };
    const someEnabledGroups = getGroupIdsByRule(rulesConfig, "some-enabled");

    Object.values(someEnabledGroups).forEach((groupIds) => {
        const enabledCount = groupIds.reduce((count, id) => {
            return normalizedOptions[id] ? count + 1 : count;
        }, 0);

        if (enabledCount === 0 && groupIds.length) {
            normalizedOptions[groupIds[0]] = true;
        }
    });

    return normalizedOptions;
};

/**
 * Calculates which options should be disabled in the UI according to rules.
 * For "some-enabled" group, if there is only one enabled option,
 * that option is marked as disabled (to prevent turning all options off).
 * Note: This should be changed into a switch button in the future to avoid this kind of logic.
 *
 * @example
 * const input = { "show-names": true, "show-roles": false };
 * const output = getCustomiseLabelsDisabledOptions(input);
 * // output: { "show-names": true }
 */
export const getCustomiseLabelsDisabledOptions = (
    options: TCustomiseLabelsOptions,
    rulesConfig: readonly TCustomiseLabelsOption[] = customiseLabelsOptions
): TCustomiseLabelsDisabledOptions => {
    const disabledOptions: TCustomiseLabelsDisabledOptions = {};
    const someEnabledGroups = getGroupIdsByRule(rulesConfig, "some-enabled");

    Object.values(someEnabledGroups).forEach((groupIds) => {
        const enabledIds = groupIds.filter((id) => Boolean(options[id]));

        if (enabledIds.length === 1) {
            disabledOptions[enabledIds[0]] = true;
        }
    });

    return disabledOptions;
};
